// Integración con el componente Embed de Mobbex (modal/iframe hosteado por
// Mobbex). Carga el script una sola vez y expone `openMobbexCheckout` para
// abrir un checkout/suscripción con los ids que devuelve el backend.
//
// NOTA: en la etapa de PRUEBA actual (mock, sin credenciales) este código NO
// se ejecuta — la página detecta `mock: true` y simula el resultado. Queda
// listo para cuando exista el backend real. Verificar la versión exacta del
// script y la firma de las opciones contra la doc oficial de Mobbex
// (https://mobbex.dev/en-embedded-integration) al conectar credenciales.

const EMBED_SRC = 'https://res.mobbex.com/js/embed/mobbex.embed@1.2.0.js';

let embedScriptPromise: Promise<void> | null = null;

/** Carga (idempotente) el script Embed de Mobbex. */
function loadEmbedScript(): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Mobbex Embed solo corre en el browser.'));
  }
  if ((window as any).MobbexEmbed) return Promise.resolve();
  if (embedScriptPromise) return embedScriptPromise;

  embedScriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = EMBED_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      embedScriptPromise = null; // permitir reintento
      reject(new Error('No se pudo cargar el script de Mobbex.'));
    };
    document.body.appendChild(script);
  });
  return embedScriptPromise;
}

export interface OpenCheckoutHandlers {
  /** Se dispara al cerrar el botón de resultado (fin del flujo). */
  onResult: (data: unknown) => void;
  /** Resultado del pago (id de operación, status, etc.). */
  onPayment?: (data: unknown) => void;
  onOpen?: () => void;
  onClose?: (cancelled?: boolean) => void;
  onError?: (err: unknown) => void;
}

export interface MobbexCheckoutIds {
  /** id de la suscripción (plan). */
  subscriptionId: string;
  /** id del suscriptor (sid). Presente en flujos de suscripción. */
  subscriberId?: string;
}

/**
 * Abre el Embed de Mobbex para una suscripción/checkout. Carga el script si
 * hace falta. Lanza si el script no carga o el global no está disponible.
 */
export async function openMobbexCheckout(
  ids: MobbexCheckoutIds,
  handlers: OpenCheckoutHandlers,
): Promise<void> {
  await loadEmbedScript();
  const MobbexEmbed = (window as any).MobbexEmbed;
  if (!MobbexEmbed?.init) {
    throw new Error('MobbexEmbed no está disponible tras cargar el script.');
  }

  const options = {
    id: ids.subscriptionId,
    // Suscripción → pasa sid + type 'subscriber_source'. Checkout simple →
    // solo type 'checkout'.
    ...(ids.subscriberId
      ? { sid: ids.subscriberId, type: 'subscriber_source' }
      : { type: 'checkout' }),
    onResult: handlers.onResult,
    onPayment: handlers.onPayment ?? (() => {}),
    onOpen: handlers.onOpen ?? (() => {}),
    onClose: handlers.onClose ?? (() => {}),
    onError: handlers.onError ?? (() => {}),
  };

  const button = MobbexEmbed.init(options);
  button.open();
}
